import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'daily-reveal-muted';

type AudioSettingsContextValue = {
  isMuted: boolean;
  setMuted: (muted: boolean) => void;
  toggleMuted: () => void;
};

const AudioSettingsContext = createContext<AudioSettingsContextValue | undefined>(undefined);

export function AudioSettingsProvider({ children }: { children: ReactNode }) {
  const [isMuted, setIsMuted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored == null) return true;
    return stored === 'true';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, String(isMuted));
  }, [isMuted]);

  const value = useMemo<AudioSettingsContextValue>(
    () => ({
      isMuted,
      setMuted: setIsMuted,
      toggleMuted: () => setIsMuted(prev => !prev),
    }),
    [isMuted]
  );

  return <AudioSettingsContext.Provider value={value}>{children}</AudioSettingsContext.Provider>;
}

export function useAudioSettings() {
  const context = useContext(AudioSettingsContext);
  if (!context) {
    throw new Error('useAudioSettings must be used within AudioSettingsProvider');
  }
  return context;
}
